import { filter as lodashFilter, size, split, uniq } from "lodash-es"

import { getNodeBody, getNodeSize } from "./diagramEditorModel"
import { resolvePrefs } from "./types"
import type { DiagramEdge, DiagramNode, DiagramSpec, DiagramView } from "./types"

export type DiagramQualitySeverity = "error" | "warning" | "suggestion"

export type DiagramQualityCode =
  | "edge-label-structural"
  | "edge-label-long"
  | "edge-label-density"
  | "node-empty"
  | "node-dense-without-tooltip"
  | "node-overlap"
  | "child-outside-section"
  | "section-too-small"
  | "edge-crosses-node"
  | "edge-crossing-density"
  | "hidden-connected-context"
  | "node-tooltip-duplicates-body"
  | "orphan-node"

export interface DiagramQualityIssue {
  id: string
  code: DiagramQualityCode
  severity: DiagramQualitySeverity
  message: string
  nodeIds?: string[]
  edgeIds?: string[]
  action?: string
}

interface Rect {
  id: string
  x: number
  y: number
  width: number
  height: number
  node: DiagramNode
}

const STRUCTURAL_EDGE_LABELS = new Set(["line", "connect", "connects", "connected", "uses", "feeds", "to", "from"])
const EDGE_LABEL_WORD_LIMIT = 2
const MAX_VISIBLE_EDGE_LABEL_RATIO = 0.35
const DENSE_NODE_BODY_CHARS = 120
const MAX_TOOLTIP_VISIBLE_WORD_OVERLAP = 0.4
const SECTION_PADDING_X = 28
const SECTION_PADDING_TOP = 60
const SECTION_PADDING_BOTTOM = 28
const MIN_OVERLAP_AREA = 240

function getVisibleNodeIds(diagram: DiagramSpec, view?: DiagramView) {
  const layerIds = new Set(view?.layers ?? diagram.layers.map((layer) => layer.id))
  const viewNodeIds = view?.nodes ? new Set(view.nodes) : null
  return new Set(
    diagram.nodes
      .filter((node) => layerIds.has(node.layer))
      .filter((node) => !viewNodeIds || viewNodeIds.has(node.id))
      .map((node) => node.id),
  )
}

function getNodePosition(node: DiagramNode, diagram: DiagramSpec) {
  const ownPosition = node.position ?? { x: 0, y: 0 }
  if (!node.parentNode) return ownPosition

  const parent = diagram.nodes.find((candidate) => candidate.id === node.parentNode)
  const parentPosition = parent?.position ?? { x: 0, y: 0 }
  return {
    x: parentPosition.x + ownPosition.x,
    y: parentPosition.y + ownPosition.y,
  }
}

function getNodeRects(diagram: DiagramSpec, visibleNodeIds: Set<string>) {
  const prefs = resolvePrefs(diagram.preferences)
  return diagram.nodes
    .filter((node) => visibleNodeIds.has(node.id))
    .map((node) => {
      const size = getNodeSize(node, prefs)
      const position = getNodePosition(node, diagram)
      return {
        id: node.id,
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height,
        node,
      }
    })
}

function rectsOverlap(left: Rect, right: Rect) {
  const xOverlap = Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x)
  const yOverlap = Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y)
  if (xOverlap <= 0 || yOverlap <= 0) return false
  return xOverlap * yOverlap >= MIN_OVERLAP_AREA
}

function isAncestorPair(left: DiagramNode, right: DiagramNode) {
  return left.id === right.parentNode || right.id === left.parentNode
}

function getRectCenter(rect: Rect) {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  }
}

function orientation(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }) {
  return (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y)
}

function segmentsIntersect(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
  d: { x: number; y: number },
) {
  const left = orientation(a, b, c)
  const right = orientation(a, b, d)
  const top = orientation(c, d, a)
  const bottom = orientation(c, d, b)
  return left * right < 0 && top * bottom < 0
}

function segmentIntersectsRect(start: { x: number; y: number }, end: { x: number; y: number }, rect: Rect) {
  const topLeft = { x: rect.x, y: rect.y }
  const topRight = { x: rect.x + rect.width, y: rect.y }
  const bottomLeft = { x: rect.x, y: rect.y + rect.height }
  const bottomRight = { x: rect.x + rect.width, y: rect.y + rect.height }

  return (
    segmentsIntersect(start, end, topLeft, topRight) ||
    segmentsIntersect(start, end, topRight, bottomRight) ||
    segmentsIntersect(start, end, bottomRight, bottomLeft) ||
    segmentsIntersect(start, end, bottomLeft, topLeft)
  )
}

function getVisibleEdges(diagram: DiagramSpec, visibleNodeIds: Set<string>) {
  return diagram.edges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
}

function issue(issue: Omit<DiagramQualityIssue, "id">): DiagramQualityIssue {
  const nodeKey = issue.nodeIds?.join("-") ?? "none"
  const edgeKey = issue.edgeIds?.join("-") ?? "none"
  return {
    ...issue,
    id: `${issue.code}:${nodeKey}:${edgeKey}`,
  }
}

function getEdgeLabelIssues(edges: DiagramEdge[]) {
  const issues: DiagramQualityIssue[] = []
  const labeledEdges = edges.filter((edge) => edge.label?.trim())

  for (const edge of labeledEdges) {
    const label = edge.label?.trim() ?? ""
    const normalized = label.toLowerCase()
    const wordCount = normalized.split(/\s+/).filter(Boolean).length

    if (STRUCTURAL_EDGE_LABELS.has(normalized)) {
      issues.push(
        issue({
          code: "edge-label-structural",
          severity: "warning",
          message: `Edge "${edge.id}" uses structural label "${label}".`,
          edgeIds: [edge.id],
          action: "Remove the label or move explanation into a node tooltip.",
        }),
      )
    } else if (wordCount > EDGE_LABEL_WORD_LIMIT) {
      issues.push(
        issue({
          code: "edge-label-long",
          severity: "warning",
          message: `Edge "${edge.id}" label is too long for canvas scanning.`,
          edgeIds: [edge.id],
          action: "Use 1-2 branch words or move detail to a tooltip.",
        }),
      )
    }
  }

  if (edges.length > 0 && labeledEdges.length / edges.length > MAX_VISIBLE_EDGE_LABEL_RATIO) {
    issues.push(
      issue({
        code: "edge-label-density",
        severity: "suggestion",
        message: "Too many visible edges are labeled.",
        edgeIds: labeledEdges.map((edge) => edge.id),
        action: "Keep labels only for branch semantics like yes, no, fail, retry, async.",
      }),
    )
  }

  return issues
}

function normalizedWords(value: string) {
  const normalized = value.toLowerCase().replace(/[^a-z0-9\s]/g, " ")
  return lodashFilter(split(normalized, /\s+/), (word) => {
    return size(word) > 2
  })
}

function tooltipRepeatsVisibleCopy(node: DiagramNode) {
  if (size(node.tooltip?.trim()) === 0) return false

  const visibleWords = new Set(normalizedWords(getNodeBody(node)))
  const tooltipWords = uniq(normalizedWords(node.tooltip ?? ""))
  if (size(visibleWords) === 0 || size(tooltipWords) === 0) return false

  const repeatedWords = lodashFilter(tooltipWords, (word) => {
    return visibleWords.has(word)
  })
  return size(repeatedWords) / size(tooltipWords) > MAX_TOOLTIP_VISIBLE_WORD_OVERLAP
}

function getNodeContentIssues(nodes: DiagramNode[]) {
  const issues: DiagramQualityIssue[] = []
  for (const node of nodes) {
    const body = getNodeBody(node).trim()
    if (!body) {
      issues.push(
        issue({
          code: "node-empty",
          severity: "warning",
          message: `Node "${node.id}" has no meaningful body.`,
          nodeIds: [node.id],
          action: "Add a short node body.",
        }),
      )
      continue
    }

    if (body.length >= DENSE_NODE_BODY_CHARS && !node.tooltip) {
      issues.push(
        issue({
          code: "node-dense-without-tooltip",
          severity: "suggestion",
          message: `Node "${node.id}" is dense but has no tooltip.`,
          nodeIds: [node.id],
          action: "Add different hover-only detail in tooltip and keep the visible body scannable.",
        }),
      )
    }

    if (tooltipRepeatsVisibleCopy(node)) {
      issues.push(
        issue({
          code: "node-tooltip-duplicates-body",
          severity: "error",
          message: `Node "${node.id}" tooltip repeats visible box copy.`,
          nodeIds: [node.id],
          action: "Rewrite tooltip with source, owner, caveat, metric, failure mode, or why it matters.",
        }),
      )
    }
  }

  return issues
}

function getGeometryIssues(diagram: DiagramSpec, rects: Rect[], edges: DiagramEdge[]) {
  const issues: DiagramQualityIssue[] = []
  const rectById = new Map(rects.map((rect) => [rect.id, rect]))

  for (let leftIndex = 0; leftIndex < rects.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < rects.length; rightIndex += 1) {
      const left = rects[leftIndex]
      const right = rects[rightIndex]
      if (!left || !right || isAncestorPair(left.node, right.node)) continue
      if (rectsOverlap(left, right)) {
        issues.push(
          issue({
            code: "node-overlap",
            severity: "error",
            message: `Nodes "${left.id}" and "${right.id}" overlap.`,
            nodeIds: [left.id, right.id],
            action: "Move one node, auto-format the canvas, or use z-order only for intentional overlap.",
          }),
        )
      }
    }
  }

  for (const section of diagram.nodes.filter((node) => node.isGroup)) {
    const sectionRect = rectById.get(section.id)
    if (!sectionRect) continue
    const children = diagram.nodes.filter((node) => node.parentNode === section.id)
    if (children.length === 0) continue

    let maxChildRight = SECTION_PADDING_X
    let maxChildBottom = SECTION_PADDING_TOP
    for (const child of children) {
      const childSize = getNodeSize(child, resolvePrefs(diagram.preferences))
      const childPosition = child.position ?? { x: 0, y: 0 }
      const outside =
        childPosition.x < 0 ||
        childPosition.y < 0 ||
        childPosition.x + childSize.width > sectionRect.width ||
        childPosition.y + childSize.height > sectionRect.height
      maxChildRight = Math.max(maxChildRight, childPosition.x + childSize.width + SECTION_PADDING_X)
      maxChildBottom = Math.max(maxChildBottom, childPosition.y + childSize.height + SECTION_PADDING_BOTTOM)

      if (outside) {
        issues.push(
          issue({
            code: "child-outside-section",
            severity: "error",
            message: `Child "${child.id}" sits outside section "${section.id}".`,
            nodeIds: [section.id, child.id],
            action: "Resize section bounds or detach the child.",
          }),
        )
      }
    }

    if (maxChildRight > sectionRect.width || maxChildBottom > sectionRect.height) {
      issues.push(
        issue({
          code: "section-too-small",
          severity: "warning",
          message: `Section "${section.id}" is tight around its children.`,
          nodeIds: [section.id, ...children.map((child) => child.id)],
          action: "Use Fix section bounds.",
        }),
      )
    }
  }

  let edgeCrossings = 0
  const edgeSegments = edges
    .map((edge) => {
      const source = rectById.get(edge.source)
      const target = rectById.get(edge.target)
      if (!source || !target) return null
      return {
        edge,
        start: getRectCenter(source),
        end: getRectCenter(target),
      }
    })
    .filter((segment): segment is NonNullable<typeof segment> => Boolean(segment))

  for (const segment of edgeSegments) {
    const crossingNode = rects.find((rect) => {
      if (rect.id === segment.edge.source || rect.id === segment.edge.target || rect.node.isGroup) return false
      return segmentIntersectsRect(segment.start, segment.end, rect)
    })
    if (crossingNode) {
      issues.push(
        issue({
          code: "edge-crosses-node",
          severity: "warning",
          message: `Edge "${segment.edge.id}" crosses node "${crossingNode.id}".`,
          nodeIds: [crossingNode.id],
          edgeIds: [segment.edge.id],
          action: "Move nodes, use auto-format, or choose a cleaner view.",
        }),
      )
    }
  }

  for (let leftIndex = 0; leftIndex < edgeSegments.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < edgeSegments.length; rightIndex += 1) {
      const left = edgeSegments[leftIndex]
      const right = edgeSegments[rightIndex]
      if (!left || !right) continue
      const sharedEndpoint =
        left.edge.source === right.edge.source ||
        left.edge.source === right.edge.target ||
        left.edge.target === right.edge.source ||
        left.edge.target === right.edge.target
      if (sharedEndpoint) continue
      if (segmentsIntersect(left.start, left.end, right.start, right.end)) edgeCrossings += 1
    }
  }

  if (edgeCrossings > Math.max(2, edges.length)) {
    issues.push(
      issue({
        code: "edge-crossing-density",
        severity: "warning",
        message: `Visible view has ${edgeCrossings} edge crossings.`,
        edgeIds: edges.map((edge) => edge.id),
        action: "Auto-format or split this into focused views.",
      }),
    )
  }

  return issues
}

function getContextIssues(diagram: DiagramSpec, visibleNodeIds: Set<string>, edges: DiagramEdge[]) {
  const issues: DiagramQualityIssue[] = []
  for (const edge of diagram.edges) {
    const sourceVisible = visibleNodeIds.has(edge.source)
    const targetVisible = visibleNodeIds.has(edge.target)
    if (sourceVisible === targetVisible) continue
    const visibleId = sourceVisible ? edge.source : edge.target
    const hiddenId = sourceVisible ? edge.target : edge.source
    issues.push(
      issue({
        code: "hidden-connected-context",
        severity: "suggestion",
        message: `Node "${visibleId}" is connected to hidden node "${hiddenId}".`,
        nodeIds: [visibleId, hiddenId],
        edgeIds: [edge.id],
        action: "Enable context nodes or add the neighbor to this view.",
      }),
    )
  }

  const connectedNodeIds = new Set(edges.flatMap((edge) => [edge.source, edge.target]))
  for (const nodeId of visibleNodeIds) {
    const node = diagram.nodes.find((candidate) => candidate.id === nodeId)
    if (!node || node.isGroup || connectedNodeIds.has(node.id)) continue
    issues.push(
      issue({
        code: "orphan-node",
        severity: "suggestion",
        message: `Node "${node.id}" has no visible edges.`,
        nodeIds: [node.id],
        action: "Connect it, move it into a focused view, or leave it if it is intentionally standalone.",
      }),
    )
  }

  return issues
}

export function analyzeDiagramQuality(diagram: DiagramSpec, viewId = diagram.defaultView) {
  const view = diagram.views.find((candidate) => candidate.id === viewId) ?? diagram.views[0]
  const visibleNodeIds = getVisibleNodeIds(diagram, view)
  const visibleNodes = diagram.nodes.filter((node) => visibleNodeIds.has(node.id))
  const visibleEdges = getVisibleEdges(diagram, visibleNodeIds)
  const rects = getNodeRects(diagram, visibleNodeIds)
  const issues = [
    ...getEdgeLabelIssues(visibleEdges),
    ...getNodeContentIssues(visibleNodes),
    ...getGeometryIssues(diagram, rects, visibleEdges),
    ...getContextIssues(diagram, visibleNodeIds, visibleEdges),
  ]

  return {
    ok: issues.filter((item) => item.severity === "error").length === 0,
    viewId: view?.id ?? viewId,
    counts: {
      errors: issues.filter((item) => item.severity === "error").length,
      warnings: issues.filter((item) => item.severity === "warning").length,
      suggestions: issues.filter((item) => item.severity === "suggestion").length,
    },
    issues,
  }
}
